/**
 * ProfilePage – progress + language direction + tutor level + VOICE picker.
 *
 * The Voice card lets users choose the TTS voice (default: `shimmer` — bright
 * and gentle) and the speaking speed. These persist server-side so every
 * subsequent /api/tts call uses them automatically.
 *
 * The Level card lets users re-pick comfort level (1..5) without having to
 * rerun the full onboarding flow — a common tweak after a few sessions.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CaretLeft, Flame, Clock, ChatCircleDots, Trophy, SignOut,
  SpeakerHigh, Play, SpinnerGap, CheckCircle, GraduationCap, CreditCard,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// Curated to what we think feels teacher-like and NOT aggressive.
// `coral` kept for users who liked the previous warm/lower tone.
const VOICES = [
  { id: 'shimmer', label: 'Shimmer',  hint: 'Bright & gentle (recommended)' },
  { id: 'sage',    label: 'Sage',     hint: 'Wise and measured' },
  { id: 'echo',    label: 'Echo',     hint: 'Smooth, calm' },
  { id: 'nova',    label: 'Nova',     hint: 'Energetic, upbeat' },
  { id: 'fable',   label: 'Fable',    hint: 'Expressive, storytelling' },
  { id: 'coral',   label: 'Coral',    hint: 'Warm and confident' },
  { id: 'alloy',   label: 'Alloy',    hint: 'Neutral, balanced' },
  { id: 'ash',     label: 'Ash',      hint: 'Clear, articulate' },
  { id: 'onyx',    label: 'Onyx',     hint: 'Deep, authoritative' },
];

// Phase 3 — Teacher instruction-language picker. Empty string = "use the
// CEFR default" (the backend resolves it). Order: native → full immersion.
const INSTRUCTION_LANG_OPTIONS = [
  { value: '',                label: 'Auto (recommended)',           hint: 'Match my CEFR level — most learners stay here' },
  { value: 'native',          label: 'All in my native language',    hint: 'Best for true beginners' },
  { value: 'mostly_native',   label: 'Mostly native, some target',   hint: 'Comfortable for A1' },
  { value: 'mixed',           label: 'Mixed (50/50)',                hint: 'A2 / early B1' },
  { value: 'mostly_target',   label: 'Mostly target, some native',   hint: 'Confident B1 / B2' },
  { value: 'full_immersion',  label: 'Full immersion',               hint: 'Advanced — no native at all' },
];

const SPEEDS = [
  { value: 0.75, label: '0.75× (slow)' },
  { value: 0.85, label: '0.85×' },
  { value: 0.95, label: '0.95× (recommended)' },
  { value: 1.00, label: '1.00× (natural)' },
  { value: 1.10, label: '1.10× (faster)' },
];

const LEVELS = [
  { v: 1, label: '1st-grade teacher', hint: '3–6 word sentences, present tense only' },
  { v: 2, label: 'Elementary', hint: 'Short sentences, common words' },
  { v: 3, label: 'Intermediate', hint: 'Natural but clear' },
  { v: 4, label: 'Upper-intermediate', hint: 'Idioms and varied tenses' },
  { v: 5, label: 'Advanced', hint: 'Full native speed' },
];

const PREVIEW_TEXT_ES = '¡Hola! Me llamo LingoFlow. Vamos a practicar juntos. ¿Estás listo?';
const PREVIEW_TEXT_EN = 'Hi! I am LingoFlow. Let us practice together. Are you ready?';

export default function ProfilePage() {
  const { user, logout, patchUser } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [savedVoice, setSavedVoice] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [savedLevel, setSavedLevel] = useState(false);
  const audioRef = useRef(null);

  // Local draft state so changes feel instant; persist on button click.
  const [voice, setVoice] = useState(user?.voice || 'shimmer');
  const [speed, setSpeed] = useState(user?.speech_speed ?? 0.95);
  const [comfort, setComfort] = useState(user?.comfort_level || 2);
  const [scaffoldDraft, setScaffoldDraft] = useState(!!user?.scaffold_mode);
  const [teacherDraft, setTeacherDraft] = useState(!!user?.teacher_mode);
  const [subscription, setSubscription] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: s }] = await Promise.all([
          http.get('/progress'),
          http.get('/subscriptions/me'),
        ]);
        setProgress(p);
        setSubscription(s);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  // If the user object reloads (e.g. after a refresh), sync local drafts.
  useEffect(() => {
    if (user?.voice) setVoice(user.voice);
    if (user?.speech_speed != null) setSpeed(user.speech_speed);
    if (user?.comfort_level) setComfort(user.comfort_level);
    setScaffoldDraft(!!user?.scaffold_mode);
    setTeacherDraft(!!user?.teacher_mode);
  }, [user?.voice, user?.speech_speed, user?.comfort_level, user?.scaffold_mode, user?.teacher_mode]);

  async function toggleScaffold() {
    const next = !scaffoldDraft;
    setScaffoldDraft(next);
    try {
      const { data } = await http.post('/me/scaffold', { scaffold_mode: next });
      patchUser(data);
    } catch (e) {
      setScaffoldDraft(!next);
      setError(apiErrorMessage(e));
    }
  }

  async function toggleTeacher() {
    const next = !teacherDraft;
    setTeacherDraft(next);
    try {
      const { data } = await http.post('/me/teacher-mode', { teacher_mode: next });
      patchUser(data);
    } catch (e) {
      setTeacherDraft(!next);
      setError(apiErrorMessage(e));
    }
  }

  /** Phase 3 — pick how heavily Teacher Mode leans on the user's native
   *  language for explanations vs. the target language for practice. */
  async function setInstructionLanguage(mode) {
    try {
      const { data } = await http.post(
        '/me/teacher-instruction-language',
        { teacher_instruction_language: mode },
      );
      // The endpoint returns {user, resolved_mode, default_for_cefr};
      // patchUser only needs the user shape.
      patchUser(data?.user || {});
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function cancelSubscription() {
    // Route to the dedicated unsubscribe page — reason capture, clear copy
    // about what the learner keeps, and a confirmation receipt screen. Apple
    // / Google require this UX to be discoverable and clear.
    navigate('/unsubscribe');
  }

  async function switchDirection() {
    const next = user?.practice_direction === 'en_to_es' ? 'es_to_en' : 'en_to_es';
    try {
      const { data } = await http.post('/onboarding', {
        practice_direction: next,
        comfort_level: user?.comfort_level || 2,
        baseline_heard: true,
      });
      patchUser(data);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function saveVoice() {
    setSavingVoice(true);
    setError('');
    setSavedVoice(false);
    try {
      const { data } = await http.post('/me/voice', { voice, speech_speed: speed });
      patchUser(data);
      setSavedVoice(true);
      setTimeout(() => setSavedVoice(false), 1800);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSavingVoice(false);
    }
  }

  async function saveLevel(next) {
    setComfort(next);
    try {
      const { data } = await http.post('/onboarding', {
        practice_direction: user?.practice_direction || 'en_to_es',
        comfort_level: next,
        baseline_heard: true,
      });
      patchUser(data);
      setSavedLevel(true);
      setTimeout(() => setSavedLevel(false), 1500);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function previewVoice() {
    setPreviewing(true);
    try {
      try { audioRef.current?.pause(); } catch { /* ignore */ }
      const text = (user?.practice_direction === 'es_to_en') ? PREVIEW_TEXT_EN : PREVIEW_TEXT_ES;
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text, voice, speed }),
      });
      if (!resp.ok) throw new Error('Preview failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); setPreviewing(false); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPreviewing(false); };
      await audio.play();
    } catch (e) {
      setError(e?.message || 'Preview failed');
      setPreviewing(false);
    }
  }

  return (
    <div className="lf-shell px-6 py-8" data-testid="profile-page">
      <div className="flex items-center gap-2">
        <Link to="/home" className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="profile-back">
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Your progress</div>
          <h1 className="text-2xl font-bold">{user?.name}</h1>
        </div>
      </div>

      {/* Practice direction + level chip */}
      <div className="lf-card mt-6 p-5">
        <div className="text-sm text-[var(--lf-text-muted)]">Currently practicing</div>
        <div className="mt-1 flex items-center justify-between">
          <div className="text-2xl font-bold">
            {user?.practice_direction === 'es_to_en' ? 'English' : 'Spanish'}
          </div>
          <button onClick={switchDirection} className="lf-btn lf-btn-secondary py-2 text-sm" data-testid="profile-switch-btn">
            Switch
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 lf-chip lf-chip-primary">
            <Trophy size={12} weight="fill" />
            CEFR {progress?.cefr_level || user?.cefr_level || 'A1'}
          </span>
          <span className="inline-flex items-center gap-1 lf-chip">
            TOEFL {progress?.toefl_approx || user?.toefl_approx || '0-30'}
          </span>
        </div>
      </div>

      {/* Tutor level */}
      <div className="lf-card mt-5 p-5" data-testid="profile-level-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm uppercase tracking-widest text-[var(--lf-text-muted)]">Tutor level</div>
            <div className="text-lg font-bold mt-0.5">How simple should the AI speak?</div>
          </div>
          {savedLevel ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--lf-success)]">
              <CheckCircle size={14} weight="fill" /> saved
            </span>
          ) : null}
        </div>
        <div className="mt-3 space-y-2">
          {LEVELS.map((L) => (
            <button
              key={L.v}
              onClick={() => saveLevel(L.v)}
              className={`flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition-all ${
                comfort === L.v
                  ? 'border-[var(--lf-primary)] bg-[#FFF5EE]'
                  : 'border-[var(--lf-border)] bg-[var(--lf-surface)] hover:bg-[var(--lf-surface-muted)]'
              }`}
              data-testid={`profile-level-${L.v}`}
            >
              <span className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${
                comfort === L.v ? 'bg-[var(--lf-primary)] text-white' : 'bg-[var(--lf-surface-muted)] text-[var(--lf-text)]'
              }`}>{L.v}</span>
              <div className="flex-1">
                <div className="font-semibold">{L.label}</div>
                <div className="text-xs text-[var(--lf-text-muted)]">{L.hint}</div>
              </div>
              {comfort === L.v ? <CheckCircle size={18} weight="fill" className="mt-1 text-[var(--lf-primary)]" /> : null}
            </button>
          ))}
        </div>
      </div>

      {/* Teacher Mode — auto on at level 1, toggle at higher levels */}
      <div className="lf-card mt-5 p-5" data-testid="profile-teacher-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <GraduationCap size={16} weight="duotone" className="text-[var(--lf-primary)]" />
              <div className="text-sm uppercase tracking-widest text-[var(--lf-text-muted)]">Teacher mode</div>
            </div>
            <div className="text-lg font-bold mt-0.5">Have the AI behave like an instructor</div>
            <div className="text-xs text-[var(--lf-text-muted)] mt-1">
              Lesson plan, pronunciation drills, quick Q&amp;A — great for
              beginners and anyone who wants structured learning.
              {comfort === 1 ? ' Always on at 1st-grade level.' : ''}
            </div>
          </div>
          <button
            role="switch"
            aria-checked={teacherDraft || comfort === 1}
            onClick={comfort === 1 ? () => {} : toggleTeacher}
            disabled={comfort === 1}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${
              (teacherDraft || comfort === 1) ? 'bg-[var(--lf-primary)]' : 'bg-[var(--lf-border)]'
            } ${comfort === 1 ? 'opacity-70' : ''}`}
            data-testid="profile-teacher-toggle"
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all duration-200 ${
                (teacherDraft || comfort === 1) ? 'left-5' : 'left-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Phase 3 — Teacher Instruction Language. Lets the user shift the
          balance between native-language explanations and target-language
          practice. The lesson plan + repetition phrases stay in the target
          language regardless. */}
      <div className="lf-card mt-5 p-5" data-testid="profile-instruction-language-card">
        <div className="flex items-center gap-2">
          <GraduationCap size={16} weight="duotone" className="text-[var(--lf-primary)]" />
          <div className="text-sm uppercase tracking-widest text-[var(--lf-text-muted)]">
            Teacher instruction language
          </div>
        </div>
        <div className="text-lg font-bold mt-0.5">
          How much of the lesson is in your native language?
        </div>
        <div className="text-xs text-[var(--lf-text-muted)] mt-1">
          Practice words + sentences always stay in your learning language.
          This only changes how the teacher explains things.
        </div>

        <div className="mt-3 grid grid-cols-1 gap-1.5">
          {INSTRUCTION_LANG_OPTIONS.map((opt) => {
            const isActive = (user?.teacher_instruction_language || '') === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setInstructionLanguage(opt.value)}
                className={`flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                  isActive
                    ? 'border-[var(--lf-primary)] bg-[#FFF5EE]'
                    : 'border-[var(--lf-border)] bg-[var(--lf-surface)] hover:border-[var(--lf-primary)]/40'
                }`}
                data-testid={`profile-instruction-language-${opt.value || 'auto'}`}
              >
                <div className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${
                  isActive ? 'border-[var(--lf-primary)]' : 'border-[var(--lf-border)]'
                }`}>
                  {isActive ? (
                    <div className="h-2.5 w-2.5 rounded-full bg-[var(--lf-primary)]" />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{opt.label}</div>
                  <div className="text-xs text-[var(--lf-text-muted)] truncate">
                    {opt.hint}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Scaffold (translate-along) mode */}
      <div className="lf-card mt-5 p-5" data-testid="profile-scaffold-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm uppercase tracking-widest text-[var(--lf-text-muted)]">Scaffold mode</div>
            <div className="text-lg font-bold mt-0.5">Show translation under every AI reply</div>
            <div className="text-xs text-[var(--lf-text-muted)] mt-1">
              Perfect for beginners &amp; intermediate learners — you always see
              what the AI just said in your own language.
            </div>
          </div>
          <button
            role="switch"
            aria-checked={scaffoldDraft}
            onClick={toggleScaffold}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${
              scaffoldDraft ? 'bg-[var(--lf-primary)]' : 'bg-[var(--lf-border)]'
            }`}
            data-testid="profile-scaffold-toggle"
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all duration-200 ${
                scaffoldDraft ? 'left-5' : 'left-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Voice + speed */}
      <div className="lf-card mt-5 p-5" data-testid="profile-voice-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm uppercase tracking-widest text-[var(--lf-text-muted)]">Tutor voice</div>
            <div className="text-lg font-bold mt-0.5">How should your AI partner sound?</div>
          </div>
          <SpeakerHigh size={22} weight="duotone" className="text-[var(--lf-primary)]" />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2">
          {VOICES.map((v) => (
            <button
              key={v.id}
              onClick={() => setVoice(v.id)}
              className={`flex items-center justify-between gap-3 rounded-2xl border p-3 text-left transition-all ${
                voice === v.id
                  ? 'border-[var(--lf-primary)] bg-[#FFF5EE]'
                  : 'border-[var(--lf-border)] bg-[var(--lf-surface)] hover:bg-[var(--lf-surface-muted)]'
              }`}
              data-testid={`profile-voice-${v.id}`}
            >
              <div className="flex-1">
                <div className="font-semibold">{v.label}</div>
                <div className="text-xs text-[var(--lf-text-muted)]">{v.hint}</div>
              </div>
              {voice === v.id ? <CheckCircle size={18} weight="fill" className="text-[var(--lf-primary)]" /> : null}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Speaking speed</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {SPEEDS.map((s) => (
              <button
                key={s.value}
                onClick={() => setSpeed(s.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                  Math.abs(speed - s.value) < 0.01
                    ? 'bg-[var(--lf-primary)] text-white'
                    : 'bg-[var(--lf-surface)] border border-[var(--lf-border)] text-[var(--lf-text)]'
                }`}
                data-testid={`profile-speed-${s.value}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            onClick={previewVoice}
            disabled={previewing}
            className="lf-btn lf-btn-secondary flex-1"
            data-testid="profile-voice-preview-btn"
          >
            {previewing ? <SpinnerGap size={16} className="animate-spin" /> : <Play size={14} weight="fill" />}
            Preview
          </button>
          <button
            onClick={saveVoice}
            disabled={savingVoice}
            className="lf-btn lf-btn-primary flex-[2]"
            data-testid="profile-voice-save-btn"
          >
            {savingVoice
              ? <SpinnerGap size={16} className="animate-spin" />
              : savedVoice ? <><CheckCircle size={16} weight="fill" /> Saved</> : 'Save voice'}
          </button>
        </div>
      </div>

      {/* Manage subscription — shown only when there's an active PayPal sub */}
      {subscription?.subscription_id ? (
        <div className="lf-card mt-5 p-5" data-testid="profile-subscription-card">
          <div className="flex items-center gap-2">
            <CreditCard size={16} weight="duotone" className="text-[var(--lf-primary)]" />
            <div className="text-sm uppercase tracking-widest text-[var(--lf-text-muted)]">Subscription</div>
          </div>
          <div className="mt-1 text-lg font-bold">
            Status: <span className="text-[var(--lf-primary)]">{subscription.status || 'ACTIVE'}</span>
          </div>
          {subscription.next_billing_time ? (
            <div className="text-xs text-[var(--lf-text-muted)]">
              Next charge: {new Date(subscription.next_billing_time).toLocaleDateString()}
            </div>
          ) : null}
          <div className="mt-3 text-xs text-[var(--lf-text-muted)]">
            PayPal subscription ID: <span className="font-mono">{subscription.subscription_id.slice(0, 14)}…</span>
          </div>
          <button
            onClick={cancelSubscription}
            className="lf-btn lf-btn-ghost mt-3 w-full text-sm text-[var(--lf-destructive)]"
            data-testid="profile-cancel-sub-btn"
          >
            Cancel auto-renewal
          </button>
        </div>
      ) : null}

      {/* Stats */}
      <div className="mt-5 grid grid-cols-3 gap-3">
        <Stat icon={<Flame size={18} weight="fill" />} label="Streak" value={`${progress?.streak_days ?? 0}d`} />
        <Stat icon={<Clock size={18} weight="fill" />} label="Minutes" value={`${progress?.total_minutes ?? 0}`} />
        <Stat icon={<ChatCircleDots size={18} weight="fill" />} label="Sessions" value={`${progress?.total_sessions ?? 0}`} />
      </div>

      {/* Vocab breakdown */}
      <div className="lf-card mt-5 p-5">
        <div className="text-sm uppercase tracking-widest text-[var(--lf-text-muted)]">Vocabulary</div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-center">
          <VocabChip label="New" value={progress?.vocab_counts?.new ?? 0} />
          <VocabChip label="Learning" value={progress?.vocab_counts?.learning ?? 0} />
          <VocabChip label="Learned" value={progress?.vocab_counts?.learned ?? 0} />
        </div>
      </div>

      {error ? <div className="mt-4 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

      <button
        onClick={async () => { await logout(); navigate('/', { replace: true }); }}
        className="lf-btn lf-btn-ghost mt-8 w-full text-[var(--lf-destructive)]"
        data-testid="profile-logout-btn"
      >
        <SignOut size={18} /> Sign out
      </button>
      <div className="h-10" />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="lf-card p-4 text-center">
      <div className="inline-flex items-center gap-1 text-xs text-[var(--lf-text-muted)]">{icon}{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}

function VocabChip({ label, value }) {
  return (
    <div className="rounded-xl bg-[var(--lf-surface-muted)] p-3">
      <div className="text-xs text-[var(--lf-text-muted)]">{label}</div>
      <div className="mt-0.5 text-xl font-bold">{value}</div>
    </div>
  );
}
